import React, { useState } from 'react';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import { Platform, Pressable, ViewStyle } from 'react-native';
import { PRICING_TABLE_LAYOUT } from '@/experiences/sales-portal/domain/pricing/constants';
import { TABLE_HEADER_BORDER_WIDTH } from '@experiences/components/table/table-header-row';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';

interface Props<T> {
  index: number;
  isDeltaColumn: (columnId: string) => boolean;
  isLeftAlignedColumn: (columnId: string) => boolean;
  onPress: () => void;
  row: Row<T>;
  testID?: string;
}

const CollapsibleStickyHeaderRowImpl = <T,>({
  index,
  isDeltaColumn,
  isLeftAlignedColumn,
  onPress,
  row,
  testID,
}: Props<T>): React.ReactElement => {
  const [styles] = useStyles();
  const [alignmentStyles] = usePricingCellAlignmentStyles();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={styles.stickyGroupHeader}
      tabIndex={-1}
      testID={testID}
    >
      <TableRow index={index} key={row.id}>
        {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
          const alignmentStyle = getCellAlignmentStyle({
            isDelta: isDeltaColumn(cell.column.id),
            isLeftAligned: isLeftAlignedColumn(cell.column.id),
            styles: alignmentStyles,
          });
          return (
            <TableCell
              cell={cell}
              key={cell.id}
              style={{
                ...alignmentStyle,
                ...(isHovered ? styles.hovered : null),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
    </Pressable>
  );
};

export const CollapsibleStickyHeaderRow = React.memo(
  CollapsibleStickyHeaderRowImpl,
) as typeof CollapsibleStickyHeaderRowImpl;

const useStyles = createStyles((theme) => ({
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  stickyGroupHeader: {
    ...Platform.select({
      web: {
        position: 'sticky' as ViewStyle['position'],
        top: PRICING_TABLE_LAYOUT.HEADER_HEIGHT + TABLE_HEADER_BORDER_WIDTH,
      },
    }),
    zIndex: 1,
  },
}));
